/**
 * День приёма пищи — общий для всех мест, где еду записывают (07.10.2026).
 *
 * Раньше везде стоял выбор «Сегодня / Вчера». Слава думал, что это приучит записывать
 * вовремя, но вышло просто неудобно: из дневника открываешь завтрашний день, жмёшь
 * «добавить», а запись уходит на сегодня. Теперь день — смещением от сегодня:
 * 0 сегодня, -1 вчера, +1 завтра.
 *
 * Вперёд — не дальше двух дней, как в чате с Дофи. Назад полоска показывает неделю;
 * из дневника можно прийти и с более ранней датой.
 */

export const MAX_FORWARD_DAYS = 2;
export const STRIP_BACK_DAYS = 6;

// Время приёма не в «сегодня» ставится по типу — как у записей из чата: завтрак
// или ужин в полдень выглядели бы в дневнике ошибкой.
const MEAL_HOUR: Record<string, number> = { breakfast: 8, lunch: 13, dinner: 19, snack: 16 };

export function dateByOffset(offset: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d;
}

/** Дата из адреса (yyyy-MM-dd или старое 'yesterday') → смещение от сегодня. */
export function offsetFromParam(param: string | null): number {
  if (!param) return 0;
  if (param === 'yesterday') return -1; // старые ссылки из дневника
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(param);
  if (!m) return 0;
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const diff = Math.round((target.getTime() - dateByOffset(0).getTime()) / 86400000);
  return Number.isFinite(diff) ? Math.min(MAX_FORWARD_DAYS, diff) : 0;
}

/** «за вчера», «на завтра», «на пт, 9 октября» — для кнопки и подтверждения. Сегодня — пусто. */
export function dayPhrase(offset: number): string {
  if (offset === 0) return '';
  if (offset === -1) return 'за вчера';
  if (offset === 1) return 'на завтра';
  if (offset === 2) return 'на послезавтра';
  const text = dateByOffset(offset).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' });
  return `${offset < 0 ? 'за' : 'на'} ${text}`;
}

/**
 * Момент приёма для выбранного дня. Сегодня — undefined: «сейчас» проставит тот,
 * кто сохраняет. Любой другой день — в час, обычный для этого приёма.
 * Запись на будущий день сервер сам придержит для серии и достижений
 * до наступления дня (миграция 057).
 */
export function consumedAtFor(offset: number, mealType: string): Date | undefined {
  if (offset === 0) return undefined;
  const d = dateByOffset(offset);
  d.setHours(MEAL_HOUR[mealType] ?? 13, 0, 0, 0);
  return d;
}

/** Приём записан на день, который ещё не наступил. */
export function isPlannedAhead(consumedAt?: Date | null): boolean {
  return !!consumedAt && consumedAt.getTime() >= dateByOffset(1).getTime();
}
